import {
  AdminStore,
  AdminStoresPage,
  OwnerStore,
  PublicStore,
  PublicStoreProgress,
  StoreAchievement,
  StoreProgress,
  StoreRank,
} from '@lib/types';

export type PublicStoreDTO = PublicStore;

export type OwnerStoreDTO = OwnerStore;

export type AdminStoreDTO = AdminStore;

export interface AdminStoresPageDTO {
  items: AdminStoreDTO[];
  total: number;
  page: number;
  limit: number;
}

interface StoreAchievementDTO {
  code: string;
  label: string;
  description: string;
  featured: boolean;
  awardedAt: string;
}

export interface PublicStoreProgressDTO {
  rank: StoreRank;
  label: string;
  achievements: StoreAchievementDTO[];
}

export interface StoreProgressDTO extends PublicStoreProgressDTO {
  totalXp: number;
  currentRankMinXp: number;
  nextRank?: StoreRank;
  nextRankLabel?: string;
  nextRankXp?: number;
  xpRemaining: number;
  progressPercentage: number;
  hasUnseenPromotion: boolean;
  objectives: Array<{
    code: string;
    label: string;
    current: number;
    target: number;
    completed: boolean;
  }>;
}

const createStoreAchievement = (
  dto: StoreAchievementDTO,
): StoreAchievement => ({
  code: dto.code,
  label: dto.label,
  description: dto.description,
  featured: dto.featured,
  awardedAt: dto.awardedAt,
});

export const createPublicStoreProgress = (
  dto: PublicStoreProgressDTO,
): PublicStoreProgress => ({
  rank: dto.rank,
  label: dto.label,
  achievements: dto.achievements.map((achievement) =>
    createStoreAchievement(achievement),
  ),
});

export const createStoreProgress = (dto: StoreProgressDTO): StoreProgress => ({
  ...createPublicStoreProgress(dto),
  totalXp: dto.totalXp,
  currentRankMinXp: dto.currentRankMinXp,
  nextRank: dto.nextRank,
  nextRankLabel: dto.nextRankLabel,
  nextRankXp: dto.nextRankXp,
  xpRemaining: dto.xpRemaining,
  progressPercentage: dto.progressPercentage,
  hasUnseenPromotion: dto.hasUnseenPromotion,
  objectives: dto.objectives.map((objective) => ({ ...objective })),
});

const createPublicStore = (dto: PublicStoreDTO): PublicStore => ({
  id: dto.id,
  name: dto.name,
  slug: dto.slug,
  logoUrl: dto.logoUrl,
  description: dto.description,
  active: dto.active,
  games: dto.games.map((game) => ({ ...game })),
  address: dto.address
    ? { city: { ...dto.address.city, state: { ...dto.address.city.state } } }
    : undefined,
  openedAt: dto.openedAt,
  completedSales: dto.completedSales,
  rating: { ...dto.rating },
});

export const createOwnerStore = (dto: OwnerStoreDTO): OwnerStore => ({
  ...createPublicStore(dto),
  progress: dto.progress ? createStoreProgress(dto.progress) : undefined,
  documentCnpj: dto.documentCnpj,
  corporateName: dto.corporateName,
  stateRegistration: dto.stateRegistration,
  freeShippingThreshold: dto.freeShippingThreshold,
  buyoutLimit: dto.buyoutLimit,
  shippingMethods: dto.shippingMethods.map((method) => ({ ...method })),
  address: dto.address
    ? {
        ...dto.address,
        city: {
          ...dto.address.city,
          state: { ...dto.address.city.state },
        },
      }
    : undefined,
});

export const createAdminStore = (dto: AdminStoreDTO): AdminStore => ({
  id: dto.id,
  name: dto.name,
  slug: dto.slug,
  documentCnpj: dto.documentCnpj,
  corporateName: dto.corporateName,
  stateRegistration: dto.stateRegistration,
  active: dto.active,
  openedAt: dto.openedAt,
});

export const createAdminStoresPage = (
  dto: AdminStoresPageDTO,
): AdminStoresPage => ({
  items: dto.items.map(createAdminStore),
  total: dto.total,
  page: dto.page,
  limit: dto.limit,
});

export const createPublicStores = (stores: PublicStoreDTO[]) =>
  stores.map(createPublicStore);

export { createPublicStore };
